import React, {
  lazy, Suspense, useEffect, useState,
} from 'react';
import { useSelector as useReduxSelector } from 'react-redux';
import {
  Selector,
  SelectorContent,
  useSelectorMachineContext,
  Pricing as PIMPricing,
  useSyncSelectionToUrl,
  usePricesMachineContext,
} from '@printdeal/product-selector';
import { Heading, Spinner } from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import { useExperiment, useFeatureConfig } from '@printdeal/feature-management/react';
import queryString from 'query-string';
import { useLocation } from '@gatsbyjs/reach-router';
import { DELIVERY_TOOLS } from '@printdeal/business-logic';
import { ErrorManagement } from '@printdeal/error-management/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import SelectorError from '../SelectorError';
import { IProductPage, IPortalConfiguration, IProductRoute } from '../../../../../types/product/productDetailPage';
import { RootState } from '../../../../../store';
import ProductJsonLd from '../../../detail/ProductJsonLd';
import ClientOnly from '../../../../ClientOnly';
import { Pricing } from './PriceSection';
import { ProductRoute, ProductRouteItem } from '../ProductRoute';
import { validateRoutes } from '../../helpers/ProductRouteHelper';
import { useServicesPrice } from '../../../../../hooks/query/useServicesPrice';
import { LatestSelection } from '../../../detail/advanced/LatestSelection';
import ResetSelectorButton from '../ResetSelectorButton';
import type { SelectedDeliveryTool } from '../../../../../types/file-delivery/FileDelivery';
import { ReviewPageProductSummary } from '../Summary/ReviewPageProductSummary';
import { AlternativeUploadFileCheck } from './AlternativeUploadFileCheck';
import { windowPresent } from '../../../../../helpers/window';
import { getStore } from '../../../../../helpers/env';
import { FileDeliveryAndServiceWrapper } from '../FileDeliveryAndServiceSelection/FileDeliveryAndServiceWrapper';
import ProductSummaryExp1862 from './ProductSummaryExp1862';
import { ServiceFactory } from '../../../../../services/ServiceFactory';
import {
  InvalidateProductConfigurationPrices, ProductConfigurationPopularity,
  ProductConfigurationPrices,
} from '../../../../../services/product/DynamicProductRoutesService';
import { configurationToInternalContent } from '../../helpers/SelectorHelper';

const ProductSummary = lazy(() => import(/* webpackChunkName: "product-summary" */ '../Summary/ProductSummary'));

const store = getStore();
const getDeliveryTool = (selection: string | string[] | null) => {
  if (store === 'impressto-fr') {
    return 'editor';
  }

  if (selection) {
    return DELIVERY_TOOLS.UPLOADER_TOOL as SelectedDeliveryTool;
  }

  return undefined;
};

interface Props {
  selector: SelectorContent;
  data: {
    pageContent: IProductPage;
    portalConfiguration: IPortalConfiguration,
  };
  reviewPage?: boolean;
}

const dynamicProductRoutesService = ServiceFactory.dynamicProductRoutesService();

export const StepByStepSelectorSection = ({
  selector, data, reviewPage = false,
}: Props) => {
  const intl = useIntl();
  const location = useLocation();
  const query = queryString.parse(location.search);
  const {
    pageContent,
    pageContent: { contentful_id: productPageId, productRoutes },
    portalConfiguration: { defaultPostalCode, defaultShippingCountry },
  } = data;
  const shippingAddress = { CountryCode: defaultShippingCountry, PostalCode: defaultPostalCode };
  const [isLoadingProductRoutes, setIsLoadingProductRoutes] = useState(Boolean(productRoutes?.length));
  const [alternativeUploadService, setAlternativeUploadService] = useState<string | undefined>(windowPresent && window?.location?.search ? 'no-service' : undefined);
  const [validatedProductRoutes, setValidatedProductRoutes] = useState<ProductRouteItem[]>([]);
  const isSelectionComplete = useSelectorMachineContext((state) => state.matches({ STARTED: 'SELECTION COMPLETE' }));
  const deliveryTool = useSelectorMachineContext((state) => state.context.merchantRules.deliveryTool);
  const isDirectUploadConfigured = Boolean(deliveryTool?.directupload);
  const selectedPrice = useSelectorMachineContext((state) => state.context.price);
  const customerGroupId = useReduxSelector<RootState, string | undefined>(
    // @ts-expect-error - The customer slice needs to be converted to typescript
    ({ customer }) => customer?.customer?.customerGroup?.id,
  );
  const shouldShowProductRoutes = selector.selectionMode === 'step-by-step'
    && Boolean(validatedProductRoutes?.length);

  const { servicesPrice } = useServicesPrice({
    serviceKeys: pageContent.services?.map((service) => service.key) || [],
    productKey: selector.product.key,
    variantSku: selector.configuration.variantSku,
    variableAttributes: [],
    country: shippingAddress.CountryCode,
    postalCode: shippingAddress.PostalCode,
    customerGroupId,
  }, Boolean(pageContent.services?.length));

  const {
    experiment: exp1853, isInitialized: isExp1853Initialized,
  } = useExperiment('servicesprouts_exp1853_disable_autoscroll_at_tooling_and_services_block');
  const isExp1853Variant = Boolean(isExp1853Initialized && exp1853?.variation === 'variant');

  const { selectionAttributes, selectedPriceAttributes, prices } = usePricesMachineContext((state) => state.context);
  const { experiment: experiment499, isInitialized: experiment499Loaded } = useExperiment('base_boosters_exp499_cheapest_product_route');
  const { experiment: experiment1869, isInitialized: experiment1869Loaded } = useExperiment('base_boosters_exp1869_most_chosen_product_route');

  const cheapestLabel = intl.formatMessage({
    id: 'productConfigurator.productRoute.cheapest', defaultMessage: 'Cheapest',
  });
  const mostChosenLabel = intl.formatMessage({
    id: 'productConfigurator.productRoute.most_chosen', defaultMessage: 'Most chosen',
  });

  const { data: cheapestRoute } = useQuery({
    queryKey: ['getDynamicProductRoutes', productPageId],
    queryFn: async () => dynamicProductRoutesService.getCheapestDynamicProductRoute(productPageId),
    enabled: experiment499Loaded && experiment499?.variation === 'variant',
    retry: false,
  });

  const { data: popularRoute } = useQuery({
    queryKey: ['getMostPopularDynamicProductRoute', productPageId],
    queryFn: async () => dynamicProductRoutesService.getMostPopularDynamicProductRoute(productPageId),
    enabled: experiment1869Loaded && experiment1869?.variation === 'variant',
    retry: false,
  });

  const { mutateAsync: savePriceTable } = useMutation({
    mutationKey: ['saveProductConfigurationPrices'],
    mutationFn: async (payload: ProductConfigurationPrices) =>
      dynamicProductRoutesService.saveProductConfigurationPrices(payload),
    retry: false,
  });

  const { mutateAsync: savePriceTablePopularity } = useMutation({
    mutationKey: ['saveProductConfigurationPopularity'],
    mutationFn: async (payload: ProductConfigurationPopularity) =>
      dynamicProductRoutesService.saveProductConfigurationPopularity(payload),
    retry: false,
  });

  const { mutateAsync: invalidatePriceTable } = useMutation({
    mutationKey: ['invalidateProductConfigurationPrices'],
    mutationFn: async (payload: InvalidateProductConfigurationPrices) =>
      dynamicProductRoutesService.invalidateProductConfigurationPrices(payload),
  });

  useEffect(() => {
    const saveProductConfigurationPrices = async () => {
      if (isSelectionComplete
        && selectionAttributes
        && selectionAttributes.length > 0
        && selectedPriceAttributes === undefined
        && prices
        && prices.length > 0) {
        await savePriceTable({
          productPageId,
          configuration: selectionAttributes,
          prices,
        });
      }
    };
    saveProductConfigurationPrices().catch((error: unknown) => {
      ErrorManagement.exception({
        exception: error,
        extra: { productPageId },
        tags: {
          fileLocation: 'StepByStepSelectorSection.tsx',
          featureFunction: 'saveProductConfigurationPrices',
        },
        fingerprint: ['productConfigurationPrices'],
      });
    });
  }, [prices, selectionAttributes, isSelectionComplete]);

  useEffect(() => {
    if (!selectedPriceAttributes) {
      return;
    }

    const saveProductConfigurationPopularity = async () => {
      if (isSelectionComplete
        && selectionAttributes
        && selectionAttributes.length > 0
        && selectedPriceAttributes) {
        await savePriceTablePopularity({
          productPageId,
          configuration: selectionAttributes,
          quantity: selectedPriceAttributes,
        });
      }
    };
    saveProductConfigurationPopularity().catch((error: unknown) => {
      ErrorManagement.exception({
        exception: error,
        extra: { productPageId },
        tags: {
          fileLocation: 'StepByStepSelectorSection.tsx',
          featureFunction: 'saveProductConfigurationPopularity',
        },
        fingerprint: ['productConfigurationPopularity'],
      });
    });
  }, [selectedPriceAttributes]);

  useEffect(() => {
    const validateProductRoutes = async () => {
      const filteredRoutes = popularRoute
        ? (productRoutes ?? []).filter((route) => route.label?.type !== 'most-chosen')
        : productRoutes ?? [];
      const routes = [
        ...filteredRoutes,
        ...(popularRoute
          ? [
            {
              id: `${popularRoute.configurationHash}`,
              isDefault: false,
              label: {
                displayName: mostChosenLabel,
                type: 'most-chosen',
              },
              productCode: {
                internal: {
                  content: configurationToInternalContent(
                    popularRoute.configuration,
                    popularRoute.quantity,
                  ),
                },
              },
              routeIdentifier: `${popularRoute.configurationHash} / most-chosen / auto-generated`,
              isAutoGenerated: true,
            } as IProductRoute,
          ]
          : []),
        ...(cheapestRoute
          ? [
            {
              id: `${cheapestRoute.configurationHash}`,
              isDefault: false,
              label: {
                displayName: cheapestLabel,
                type: 'cheapest',
              },
              productCode: {
                internal: {
                  content: configurationToInternalContent(
                    cheapestRoute.configuration,
                    cheapestRoute.cheapestPriceAttributes,
                  ),
                },
              },
              routeIdentifier: `${cheapestRoute.configurationHash} / cheapest / auto-generated`,
              isAutoGenerated: true,
            } as IProductRoute,
          ]
          : []),
      ];

      if (!routes?.length) {
        return;
      }

      const validProductRoutes = await validateRoutes(routes, productPageId, customerGroupId);
      const isCheapestRouteIncluded = validProductRoutes.some((route) =>
        route.productRoute.id === cheapestRoute?.configurationHash);
      if (!isCheapestRouteIncluded && cheapestRoute) {
        await invalidatePriceTable({ productPageId, configurationHash: cheapestRoute.configurationHash });
      }

      setValidatedProductRoutes(validProductRoutes);
      setIsLoadingProductRoutes(false);
    };

    validateProductRoutes().catch((error: unknown) => {
      ErrorManagement.exception({
        exception: error,
        extra: { productPageId },
        tags: {
          fileLocation: 'StepByStepSelectorSection.tsx',
          featureFunction: 'validateProductRoutes',
        },
        fingerprint: ['productRoutes', 'validate'],
      });
    });
  }, [
    productRoutes,
    productPageId,
    customerGroupId,
    cheapestRoute,
    cheapestLabel,
    popularRoute,
  ]);

  // EXP1021 - Start
  const { isInitialized, featureConfig } = useFeatureConfig<Record<'productKeys', string[]>>('exp1021_file_delivery_choice_on_the_top');
  const isProductKeyExcluded = isInitialized && featureConfig.variables.productKeys?.includes(selector.product.key);
  const {
    experiment: exp1021, isInitialized: isExp1021Initialized,
  } = useExperiment('ninjas_exp1021_file_delivery_choice_on_the_top');
  const isExp1021Variant = Boolean(!isProductKeyExcluded && isExp1021Initialized && exp1021?.variation === 'variant');
  const servicePrices = useSelectorMachineContext((state) => state.context.servicePrices) || servicesPrice;
  const services = (servicePrices && pageContent.services && pageContent.services.map((service) => ({
    ...service,
    price: servicePrices[service.key],
  }))) || [];

  const [hasFileDeliveryChoice, setHasDeliveryToolChoice] = useState(true);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedDeliveryTool, setSelectedDeliveryTool] = useState<SelectedDeliveryTool | undefined>(
    getDeliveryTool(query.selection),
  );
  const [hadSelectionOnPageLoad] = useState(Boolean(query.selection));
  const [isChoiceSkipped, setIsChoiceSkipped] = useState(!hasFileDeliveryChoice);
  // EXP1021 - End

  useSyncSelectionToUrl();

  return (
    <>
      {isExp1021Variant && (
        <section className="pd-my-8">
          <FileDeliveryAndServiceWrapper
            productPage={pageContent}
            selector={selector}
            services={services}
            setSelectedDeliveryTool={setSelectedDeliveryTool}
            setHasDeliveryToolChoice={setHasDeliveryToolChoice}
            selectedServices={selectedServices}
            alternativeUploadService={alternativeUploadService}
            setAlternativeUploadService={setAlternativeUploadService}
            setSelectedServices={setSelectedServices}
            selectedDeliveryTool={selectedDeliveryTool}
            hasFileDeliveryChoice={hasFileDeliveryChoice}
            isChoiceSkipped={isChoiceSkipped}
            setIsChoiceSkipped={setIsChoiceSkipped}
            isDirectUploadConfigured={isDirectUploadConfigured}
            isSelectionComplete={isSelectionComplete}
            deliveryTool={deliveryTool}
          />
        </section>
      )}
      {shouldShowProductRoutes && (validatedProductRoutes.length > 0 || isLoadingProductRoutes) && (
        <div className="pd-my-4 pd-rounded pd-border-1 pd-p-3">
          <Heading as="span" size="4">
            <FormattedMessage id="product.configurator.chooseQuick" defaultMessage="Choose quick?" />
          </Heading>
          {isLoadingProductRoutes ? (
            <div className="pd-my-1 pd-flex pd-justify-center">
              <Spinner
                size="sm"
                variant="dim"
                screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
              />
            </div>
          ) : validatedProductRoutes?.map(({ productRoute, price, originalPrice }) => (
            <ProductRoute
              key={productRoute.routeIdentifier}
              productRoute={productRoute}
              selector={selector}
              price={price}
              originalPrice={originalPrice}
            />
          ))}
          {validatedProductRoutes.length > 0 && (
            <LatestSelection
              productKey={selector.product.key}
              productRoute={validatedProductRoutes[0].productRoute}
              selectorConfig={selector}
              contentfulId={productPageId}
            />
          )}
        </div>
      )}
      <div className="pd-my-4 pd-flex pd-justify-end">
        <ResetSelectorButton contentfulId={productPageId} />
      </div>
      <ClientOnly>
        <ProductJsonLd productPage={pageContent} selector={selector} />
      </ClientOnly>

      <SelectorError contentfulId={pageContent.contentful_id} />

      <Selector
        productPageId={pageContent.contentful_id}
        selector={selector}
        shippingAddress={shippingAddress}
        customerGroupId={customerGroupId}
        isReviewPage={reviewPage}
        serviceKeys={pageContent.services?.map((service) => service.key)}
      />

      {selector.product.isPimProduct ? (
        <PIMPricing />
      ) : (
        <Pricing
          product={selector.product}
          shippingAddress={shippingAddress}
          customerGroupId={customerGroupId}
          contentfulPageId={pageContent.contentful_id}
          productDiscounts={pageContent.productDiscounts}
        />
      )}

      {(!isExp1021Variant && selector.selectionMode === 'step-by-step' && selectedPrice && isSelectionComplete && !reviewPage) && (
        <Suspense>
          <div className="pd-flex pd-flex-col pd-gap-6">
            {!isDirectUploadConfigured && store !== 'impressto-fr' && (
              <FileDeliveryAndServiceWrapper
                productPage={pageContent}
                selector={selector}
                services={services}
                setSelectedDeliveryTool={setSelectedDeliveryTool}
                setHasDeliveryToolChoice={setHasDeliveryToolChoice}
                selectedServices={selectedServices}
                alternativeUploadService={alternativeUploadService}
                setAlternativeUploadService={setAlternativeUploadService}
                setSelectedServices={setSelectedServices}
                selectedDeliveryTool={selectedDeliveryTool}
                hasFileDeliveryChoice={hasFileDeliveryChoice}
                isChoiceSkipped={isChoiceSkipped}
                setIsChoiceSkipped={setIsChoiceSkipped}
                isDirectUploadConfigured={isDirectUploadConfigured}
                isSelectionComplete={isSelectionComplete}
                deliveryTool={deliveryTool}
                hadSelectionOnPageLoad={hadSelectionOnPageLoad}
              />
            )}

            {isDirectUploadConfigured && (
              <AlternativeUploadFileCheck
                services={services}
                alternativeUploadService={alternativeUploadService}
                setSelectedServices={setSelectedServices}
                setAlternativeUploadService={setAlternativeUploadService}
                setSelectedDeliveryTool={setSelectedDeliveryTool}
              />
            )}

            {((hasFileDeliveryChoice && selectedDeliveryTool)
            || (!hasFileDeliveryChoice && !deliveryTool?.servicekeys)
            || (isDirectUploadConfigured && alternativeUploadService)
            || !hasFileDeliveryChoice || isChoiceSkipped) && (
              <ProductSummaryExp1862
                hasFileDeliveryChoice={hasFileDeliveryChoice}
                alternativeUploadService={alternativeUploadService}
                deliveryTool={deliveryTool}
                isDirectUploadConfigured={isDirectUploadConfigured}
                isChoiceSkipped={isChoiceSkipped}
                productName={pageContent?.title || pageContent?.productName || ''}
                selector={selector}
                pageContent={pageContent}
                selectedDeliveryTool={hasFileDeliveryChoice ? selectedDeliveryTool : undefined}
                selectedServices={selectedServices}
                customerGroupId={customerGroupId}
                isExp1853Variant={isExp1853Variant}
              />
            )}
          </div>
        </Suspense>
      )}
      {(isExp1021Variant && selector.selectionMode === 'step-by-step' && selectedPrice && isSelectionComplete && !reviewPage
          && ((hasFileDeliveryChoice && selectedDeliveryTool) || !hasFileDeliveryChoice || isChoiceSkipped)) && (
          <Suspense>
            <ProductSummary
              productName={pageContent?.title || pageContent?.productName || ''}
              selector={selector}
              pageContent={pageContent}
              selectedDeliveryTool={selectedDeliveryTool}
              selectedServices={selectedServices}
              customerGroupId={customerGroupId}
              isExp1853Variant={isExp1853Variant}
            />
          </Suspense>
      )}

      {reviewPage && (
      <ReviewPageProductSummary pageContent={pageContent} selector={selector} productPageId={productPageId} />
      )}
    </>
  );
};
